<!--
 *
 * Copyright © 2017 Easymeeting AS. All rights reserved.
 *
 * This software is produced by Easymeeting AS.
 * Unauthorized redistribution, reproduction, or usage of
 * this software in whole or in part without the express
 * written consent of Easymeeting AS is strictly prohibited.
-->
<template>
  <div
    v-if="contact"
    class="card easy-user-card"
    :class="[{recent:recent, 'select-mode':selectMode}, profilePic ? '' : `bg-color-${colorCode}`]"
    :data-qa="dataQa"
    @click.stop="cardClick"
  >
    <div
      v-if="!imageOnly"
      class="card-body"
    >
      <div
        v-if="selectMode"
        class="form-group select-user"
        :data-qa="`${dataQa}_contact_checkbox_container`"
      >
        <FormCheckbox
          :rounded="true"
          :checked="isSelected"
          :data-qa="`${dataQa}_contact_checkbox`"
          @change="toggleSelect"
        />
      </div>
      <form-button
        v-if="contact.protocol === 'h323'"
        type="user-ctrl"
        icon="SvgNumPad"
        class="left"
        :data-qa="`${dataQa}_dtmf_btn`"
        @onClick="showDTMF"
      />
      <form-button
        v-if="isFavorited"
        type="user-ctrl"
        icon="SvgFavorited"
        class="right static"
        :data-qa="`${dataQa}_fav_btn`"
      />
      <div
        class="user-details"
        :data-qa="`${dataQa}_details_container`"
      >
        <h4
          :data-qa="`${dataQa}_contact_name`"
        >
          {{ displayName }}
        </h4>
        <h5
          v-if="contact.title"
          class="contact-title"
          :class="{'d-none': recent}"
          :data-qa="`${dataQa}_contact_title`"
        >
          {{ contact.title }}
        </h5>
        <h5
          class="contact-room"
          :class="{'d-none': recent}"
          :data-qa="`${dataQa}_contact_room`"
        >
          {{ contact.room || contact.number_short }}
        </h5>
        <h6 v-if="recent">
          <svg-icon
            v-if="contact.direction === 'in' && contact.callstatus === 'timedout'"
            class="missed"
            icon="SvgMissed"
            :data-qa="`${dataQa}_contact_recent_icon`"
          />
          <svg-icon
            v-else-if="contact.direction === 'in'"
            class="incoming"
            icon="SvgUpNav"
            :data-qa="`${dataQa}_contact_recent_icon`"
          />
          <svg-icon
            v-else-if="contact.direction === 'out'"
            class="outgoing"
            icon="SvgUpNav"
            :data-qa="`${dataQa}_contact_recent_icon`"
          />
          <DateTimeAgo
            v-if="contact.calltime"
            :date-time="contact.calltime"
            :data-qa="`${dataQa}_call_time`"
          />
        </h6>
      </div>
      <div
        v-if="!isDial && profilePic"
        class="img-blur"
        :data-qa="`${dataQa}_contact_image_blured_container`"
      >
        <div
          class="blur"
          :style="{backgroundImage: `url('${profilePic}')`}"
          :data-qa="`${dataQa}_contact_image_blured`"
        />
      </div>
      <template v-if="showCallBtn && !inRoom">
        <form-button
          v-if="isOnline"
          icon="SvgCall"
          class="btn-call btn-success"
          rounded
          :data-qa="`${dataQa}_contact_call_btn`"
          @onClick.stop="call"
        />
        <form-button
          v-else
          icon="SvgCall"
          class="btn-call"
          rounded
          :class="{'btn-danger': isBusy, 'btn-disabled': isOffline}"
          :disabled="isOffline"
          :data-qa="`${dataQa}_contact_call_btn`"
        />
      </template>
      <template v-if="inRoom && showDisconnectBtn">
        <form-button
          type="danger"
          icon="SvgCall"
          class="btn-call"
          rounded
          :data-qa="`${dataQa}_participant_remove_btn`"
          @onClick.stop="remove"
        />
      </template>
    </div>
    <div
      class="user-picture-container"
      :data-qa="`${dataQa}_contact_image_container`"
    >
      <div
        v-if="profilePic"
        class="user-picture"
        :style="{backgroundImage: `url('${profilePic}')`}"
        :data-qa="`${dataQa}_contact_image`"
      />
      <h2
        v-else
        class="center-name"
        :data-qa="`${dataQa}_contact_image`"
      >
        {{ imageOnly ? firstName.substr(0,1) : firstName }}
      </h2>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import contactMixin from '@/mixins/contact';
export default {
  name: 'ContactCard',
  mixins: [contactMixin],
  props: {
    contact: {
      type: Object,
      default: null
    },
    recent: {
      type: Boolean,
      default: false
    },
    isChat: {
      type: Boolean,
      default: false
    },
    isSearch: {
      type: Boolean,
      default: false
    },
    isDial: {
      type: Boolean,
      default: false
    },
    isSelected: {
      type: Boolean,
      default: false
    },
    inRoom: {
      type: Boolean,
      default: false
    },
    showDisconnectBtn: {
      type: Boolean,
      default: false
    },
    selectMode: {
      type: Boolean,
      default: false
    },
    imageOnly: {
      type: Boolean,
      default: false
    },
    dataQa: {
      type: String,
      default: null
    }
  },
  data () {
    return {};
  },
  computed: {
    ...mapGetters('AppStore', {
      inPhoneCall: 'inPhoneCall'
    }),
    showCallBtn () {
      if (this.isChat) {
        return false;
      }
      if (this.inPhoneCall) {
        return false;
      }
      if (this.contact.protocol && this.contact.protocol === 'webrtc') {
        return false;
      }
      return true;
    },
    displayName () {
      if (this.isDial) {
        return this.$t('APP_TEXT_DIAL_TO');
      }
      if (this.contact && this.contact.name) {
        return this.contact.name;
      }
      if (this.contact && this.contact.contact && this.contact.contact.name) {
        return this.contact.contact.name;
      }
      return '';
    },
    firstName () {
      if (this.contact.name && this.contact.name.trim().length > 0) {
        return this.contact.name.split(' ')[0];
      }
      if (this.inRoom) {
        return this.contact.name ? this.contact.name.split(' ')[0] : '';
      }
      if (this.contact.alias) {
        return this.contact.alias;
      }
      return this.recent
        ? this.contact.contact.fname ||
            (this.contact.name ? this.contact.name.split(' ')[0] : '')
        : this.contact.defaultdevice === 'H323'
          ? 'H323'
          : this.contact.fname;
    },
    profilePic () {
      return this.isDial
        ? this.$_LOGOS.DEFAULT_DIAL_IMAGE
        : this.recent
          ? this.contact.contact.profilepic || null
          : this.contact.profilepic || null;
    }
  },
  created () {
  },
  methods: {
    cardClick () {
      if (this.isChat) {
        if (this.selectMode) {
          this.toggleSelect(!this.isSelected);
        } else {
          this.chat();
        }
      } else {
        if (this.contact.protocol && this.contact.protocol === 'webrtc') {
          return false;
        }
        this.$emit('cardClick');
      }
    },
    chat () {
      this.$emit('chat', this.contact);
    },
    call () {
      if (this.isOnline) {
        this.$emit('call');
      }
    },
    remove () {
      this.$emit('remove', this.contact);
    },
    showDTMF () {
      this.$emit('dtmf', this.participant);
    },
    toggleSelect (selected) {
      if (this.selectMode) {
        this.$emit('seletionChanged', {
          contact: this.contact,
          selected
        });
      }
    }
  }
};
</script>

<style scoped>
</style>
